/**
 * Tiny in-memory GET cache so navigating between pages doesn't re-hit the
 * network for the same list within a short TTL.
 *
 * - Mutations should call invalidate('/tasks') etc.
 * - Only safe for idempotent GETs with stable paths (include query string).
 */

const store = new Map(); // path -> { exp, data, inflight }

const DEFAULT_TTL = 45_000; // 45s

export function cacheGet(path) {
  const hit = store.get(path);
  if (!hit) return null;
  if (hit.exp < Date.now()) {
    store.delete(path);
    return null;
  }
  return hit.data;
}

export function cacheSet(path, data, ttl = DEFAULT_TTL) {
  store.set(path, { exp: Date.now() + ttl, data, inflight: null });
  return data;
}

/** Dedupe concurrent identical GETs. */
export async function cachedFetch(path, fetcher, { ttl = DEFAULT_TTL, force = false } = {}) {
  if (!force) {
    const hit = cacheGet(path);
    if (hit != null) return hit;
    const existing = store.get(path);
    if (existing?.inflight) return existing.inflight;
  }

  const inflight = Promise.resolve()
    .then(fetcher)
    .then((data) => {
      cacheSet(path, data, ttl);
      return data;
    })
    .catch((err) => {
      store.delete(path);
      throw err;
    });

  store.set(path, { exp: Date.now() + ttl, data: null, inflight });
  return inflight;
}

/** Invalidate exact path, or any path starting with prefix (e.g. '/tasks'). */
export function invalidate(prefixOrPath) {
  if (!prefixOrPath) {
    store.clear();
    return;
  }
  for (const key of [...store.keys()]) {
    if (key === prefixOrPath || key.startsWith(prefixOrPath)) store.delete(key);
  }
}
